import { computed, inject, Injectable } from '@angular/core';
import { UserService } from '@dxc/data-access';
import { SelectOption } from '@shared/types';
import {
  injectQuery,
  QueryFunctionContext,
  QueryKey
} from '@tanstack/angular-query-experimental';
import { fromEvent, lastValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Centralised, cached access to the list of platform users that can be selected
 * as assignees. The query is keyed solely by `'assignee-list'` which means the
 * result is shared across every component that injects this service.
 *
 * NOTE:  – TanStack Query guarantees that a second identical query will reuse
 *         the same cache entry instead of triggering a network request.
 */
@Injectable({ providedIn: 'root' })
export class AssigneeOptionsService {
  private readonly userService = inject(UserService);

  /**
   * Internal TanStack query that performs the HTTP request exactly once per
   * browser session (while the query is cached).
   */
  private readonly _assigneeListQuery = injectQuery<SelectOption[]>(() => ({
    queryKey: ['assignee-list'] as QueryKey,
    // Use an abortable queryFn so ongoing HTTP requests are cancelled when
    // components get destroyed (Navigation / ngOnDestroy).
    queryFn: async (ctx: QueryFunctionContext<QueryKey>) => {
      const abort$ = fromEvent(ctx.signal, 'abort');
      const users = await lastValueFrom(
        this.userService.getUsersList().pipe(takeUntil(abort$))
      );
      return users.map((user) => ({
        id: user.id,
        label: `${user.first_name} ${user.last_name}`
      }));
    }
  }));

  /** Public readonly signal with the mapped SelectOption[] list. */
  readonly assigneeOptions = computed(
    () => this._assigneeListQuery.data() ?? []
  );

  /** Convenience loading indicator signal. */
  readonly loading = computed(
    () =>
      this._assigneeListQuery.isLoading()
      || this._assigneeListQuery.isFetching()
  );
}
